import { LeadDetail } from "../../../types/leadDetail";
import Field, { FieldGrid } from "./Field";
import Section from "./Section";

const fmtDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtDateTime = (iso: string | null | undefined) => {
  if (!iso) return null;
  try {
    return new Intl.DateTimeFormat("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
};

const fmtMoney = (n: number | null | undefined, currency = "EUR") => {
  if (n === null || n === undefined) return null;
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(n);
};

const fmtBool = (v: boolean | null | undefined) => {
  if (v === null || v === undefined) return null;
  return v ? "Sí" : "No";
};

export function HealthSection({ detail }: { detail: LeadDetail }) {
  const h = detail.health;
  if (!h) return null;
  return (
    <Section title="Salud y estilo de vida" accent="#dc3545">
      <FieldGrid>
        <Field label="Condición de salud" value={h.healthCondition} />
        <Field label="Nicotina" value={h.nicotine} />
        <Field label="Cantidad nicotina" value={h.nicotineAmount} />
        <Field label="Ejercicio diario" value={h.dailyExercise} />
        <Field label="Tipo ejercicio" value={h.exerciseType} />
        <Field label="Alcohol" value={h.alcohol} />
        <Field
          label="Peso"
          value={h.weight ? `${h.weight} kg` : null}
        />
        <Field
          label="Altura"
          value={h.height ? `${h.height} cm` : null}
        />
        <Field label="Discapacidad" value={h.handicapped} />
        <Field label="Enfermedad diagnosticada" value={h.diagnosedIllness} />
        <Field label="Esperando diagnóstico" value={h.awaitingDiagnosis} />
        <Field label="Enfermedades" value={h.illnesses} />
        <Field label="Hospitalización" value={h.hospitalization} />
      </FieldGrid>
    </Section>
  );
}

export function FamilyEconomySection({ detail }: { detail: LeadDetail }) {
  const f = detail.family;
  const e = detail.economy;
  if (!f && !e) return null;
  return (
    <Section title="Familia y economía" accent="#198754">
      {f && (
        <>
          <h3 style={titleStyle}>Familia</h3>
          <FieldGrid>
            <Field label="Hijos" value={f.children} />
            <Field label="Edad hijos" value={f.childrenAge} />
            <Field label="Año escolar" value={f.schoolYear} />
            <Field label="Pareja" value={f.couple} />
            <Field label="Fecha nacimiento (hijos)" value={f.dateToBirth} />
          </FieldGrid>
        </>
      )}
      {e && (
        <>
          <h3 style={titleStyle}>Economía</h3>
          <FieldGrid>
            <Field label="Ingresos" value={fmtBool(e.income)} />
            <Field label="Deuda" value={fmtBool(e.debt)} />
            <Field label="Term" value={fmtBool(e.term)} />
            <Field label="Coverage" value={fmtBool(e.coverage)} />
            <Field label="Hipoteca" value={fmtBool(e.mortgage)} />
            <Field label="Ahorros" value={fmtBool(e.savings)} />
          </FieldGrid>
        </>
      )}
    </Section>
  );
}

export function PurchaseSection({ detail }: { detail: LeadDetail }) {
  const p = detail.purchase;
  const beneficiaries = detail.beneficiaries;
  if (!p && beneficiaries.length === 0) return null;
  return (
    <Section title="Compra y beneficiarios" defaultOpen accent="#0069d9">
      {p && (
        <FieldGrid>
          <Field label="Fecha" value={fmtDateTime(p.date)} />
          <Field label="Período" value={p.period} />
          <Field
            label="Capital"
            value={p.capital !== null ? fmtMoney(p.capital) : null}
          />
          <Field label="Carrier" value={p.carrier} />
          <Field label="Tipo seguro" value={p.insuranceType} />
          <Field label="Precio final" value={fmtMoney(p.finalPrice)} />
          <Field label="Precio calculado" value={fmtMoney(p.calculatedPrice)} />
          <Field
            label="Capital inicial"
            value={p.initialCapital !== null ? fmtMoney(p.initialCapital) : null}
          />
          <Field
            label="Capital calculado"
            value={
              p.calculatedCapital !== null ? fmtMoney(p.calculatedCapital) : null
            }
          />
          <Field
            label="Último precio mostrado"
            value={fmtMoney(p.lastSelectedPrice)}
          />
          <Field label="Intención" value={p.intention} />
          <Field label="Oferta" value={p.offer} />
          <Field label="Precio actual" value={fmtMoney(p.actualPrice)} />
          <Field label="Amazon aceptado" value={p.amazonAccepted} />
          <Field label="Motivo rechazo" value={p.rejectionCause} />
        </FieldGrid>
      )}
      {beneficiaries.length > 0 && (
        <>
          <h3 style={titleStyle}>
            Beneficiarios ({beneficiaries.length})
          </h3>
          <div style={{ display: "grid", gap: "0.6rem" }}>
            {beneficiaries.map((b, i) => (
              <div
                key={i}
                style={{
                  border: "1px solid #f1f3f5",
                  borderRadius: "0.45rem",
                  padding: "0.6rem 0.85rem",
                  background: "#fafbfc",
                }}
              >
                <FieldGrid>
                  <Field label="Tipo" value={b.type} />
                  <Field label="Capital" value={b.capital} />
                  <Field
                    label="Nombre"
                    value={[b.name, b.lastName, b.lastName2]
                      .filter(Boolean)
                      .join(" ") || null}
                  />
                  <Field label="DNI / NIE" value={b.idNumber} mono />
                  <Field label="Banco" value={b.bankName} />
                  <Field label="Nº préstamo" value={b.loanMortgageNumber} />
                  <Field label="Razón social" value={b.businessName} />
                  <Field label="CIF" value={b.fiscalNumber} />
                </FieldGrid>
              </div>
            ))}
          </div>
        </>
      )}
    </Section>
  );
}

export function TrackingSection({ detail }: { detail: LeadDetail }) {
  const t = detail.track;
  if (!t) return null;
  const allEmpty =
    !t.campaign &&
    !t.awarenessSource &&
    !t.goalSource &&
    !t.awarenessLanding &&
    !t.goalLanding;
  if (allEmpty) return null;
  return (
    <Section title="Marketing tracking" accent="#6f42c1">
      <h3 style={titleStyle}>Awareness</h3>
      <FieldGrid>
        <Field label="Landing" value={t.awarenessLanding} />
        <Field label="Source" value={t.awarenessSource} />
        <Field label="Medium" value={t.awarenessMedium} />
        <Field label="Channel" value={t.awarenessChannel} />
        <Field label="Keyword" value={t.awarenessKeyword} />
        <Field label="Campaign" value={t.awarenessCampaign} />
        <Field label="Content" value={t.awarenessContent} />
      </FieldGrid>
      <h3 style={titleStyle}>Goal</h3>
      <FieldGrid>
        <Field label="Landing" value={t.goalLanding} />
        <Field label="Source" value={t.goalSource} />
        <Field label="Channel" value={t.goalChannel} />
        <Field label="Keyword" value={t.goalKeyword} />
        <Field label="Campaign" value={t.goalCampaign} />
        <Field label="Content" value={t.goalContent} />
      </FieldGrid>
      {t.campaign && (
        <>
          <h3 style={titleStyle}>General</h3>
          <FieldGrid>
            <Field label="Campaign" value={t.campaign} />
          </FieldGrid>
        </>
      )}
    </Section>
  );
}

export function BrokerSection({ detail }: { detail: LeadDetail }) {
  if (!detail.brokerName && !detail.brokerEmployeeName && !detail.brokerEmployeeEmail) {
    return null;
  }
  return (
    <Section title="Broker" accent="#fd7e14">
      <FieldGrid>
        <Field label="Broker" value={detail.brokerName} />
        <Field label="Empleado" value={detail.brokerEmployeeName} />
        <Field label="Email empleado" value={detail.brokerEmployeeEmail} copy />
      </FieldGrid>
    </Section>
  );
}

// Statuses where a policy exists (or did exist) and a cancellation row in
// the lead row actually means something. For everything else — telesubscription,
// the pre-policy "*_pending" flow, ops_review, signature_pending… — any value
// in cancellation_* / reason is leftover noise (e.g. a status revert that
// left the columns populated) and would surface a misleading red banner.
const CANCELLATION_RELEVANT_STATUSES = new Set([
  "complete",
  "canceled",
  "claim",
  "rejected",
  "insurer_review",
]);

export function CancellationCard({ detail }: { detail: LeadDetail }) {
  if (!CANCELLATION_RELEVANT_STATUSES.has(detail.status)) return null;
  const hasAny =
    detail.cancellationType ||
    detail.cancellationDate ||
    detail.cancellationRequest ||
    detail.effectiveDateOfCancellationByCarrier ||
    detail.cancellationReasons.length > 0;
  if (!hasAny) return null;
  return (
    <div
      style={{
        background: "#fde2e2",
        borderRadius: "0.55rem",
        borderLeft: "3px solid #b32424",
        padding: "0.75rem 1rem",
        marginBottom: "0.75rem",
      }}
    >
      <div
        style={{
          fontSize: ".75rem",
          fontWeight: 600,
          color: "#b32424",
          textTransform: "uppercase",
          letterSpacing: ".4px",
          marginBottom: "0.45rem",
        }}
      >
        Cancelación
      </div>
      <FieldGrid>
        <Field label="Tipo" value={detail.cancellationType} />
        <Field label="Solicitada" value={fmtDate(detail.cancellationRequest)} />
        <Field label="Efectiva" value={fmtDate(detail.cancellationDate)} />
        <Field
          label="Carrier"
          value={fmtDate(detail.effectiveDateOfCancellationByCarrier)}
        />
        <Field
          label="Motivos"
          value={
            detail.cancellationReasons.length > 0
              ? detail.cancellationReasons.join(", ")
              : null
          }
        />
      </FieldGrid>
    </div>
  );
}

// Replicates the legacy lead-detail warning banner. Gated purely on the
// telesubscription flag (contact_lead.has_telesubscription), the same signal
// the legacy details.html.twig used — NOT on status, since the flag is sticky
// and a lead can advance past the telesubscription status while still being
// priced under telesubscription rules. Wording kept verbatim from legacy.
export function TelesubscriptionBanner({ detail }: { detail: LeadDetail }) {
  if (!detail.hasTelesubscription) return null;
  return (
    <div
      style={{
        background: "#fff3cd",
        borderRadius: "0.55rem",
        borderLeft: "3px solid #b8860b",
        padding: "0.7rem 1rem",
        marginBottom: "0.75rem",
        color: "#856404",
        fontSize: ".85rem",
        fontWeight: 600,
      }}
      role="alert"
    >
      ⚠️ This lead is tarified under telesubscription rules — some data may
      appear unusual.
    </div>
  );
}

const titleStyle: React.CSSProperties = {
  margin: "1rem 0 0.3rem",
  fontSize: ".75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  color: "#0069d9",
  letterSpacing: ".5px",
  borderBottom: "1px solid #f1f3f5",
  paddingBottom: ".2rem",
};
